import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import Layouts from "../../layouts/layouts";
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsRenderer,
  InfoWindow,
  OverlayView,
} from "@react-google-maps/api";
import moment from "moment";

const containerStyle = {
  width: "100vw",
  height: "100vh",
  position: "absolute",
  top: 0,
  left: 0,
};

// ============================================================
// FLICKERING EFFECT
// ============================================================

const rippleStyle = {
  position: "absolute",
  width: "200px",
  height: "200px",
  background: "#4285F4",
  borderRadius: "50%",
  animation: "ripple 1.5s infinite",
  transform: "translate(-50%, -50%)",
  opacity: 0.4,
  pointerEvents: "none",
};

const keyframes = `
  @keyframes ripple {
    0% {
      transform: translate(-50%, -50%) scale(0.3);
      opacity: 0.6;
    }

    100% {
      transform: translate(-50%, -50%) scale(1.4);
      opacity: 0;
    }
  }
`;

export default function MyMapComponentUser() {
  const location = useLocation();
  const { userId } = useParams();

  const mapRef = useRef(null);

  const [pathCoordinates, setPathCoordinates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Multiple routes because Google Directions has waypoint limits
  const [directions, setDirections] = useState([]);

  // Google Maps loaded status
  const [googleReady, setGoogleReady] = useState(false);

  // ============================================================
  // ADD RIPPLE CSS
  // ============================================================

  useEffect(() => {
    const styleSheet = document.createElement("style");

    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;

    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // ============================================================
  // FETCH USER TRACKING FOR SELECTED DATE
  // ============================================================

  const fetchUserTrackingByDate = async (date) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found");
        return;
      }

      console.log("Fetching tracking for date:", date);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-tracking-by-date/${userId}/${date}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const trackingData = response.data.data || [];

      // ========================================================
      // SORT OLDEST -> NEWEST
      // ========================================================

      const sortedTrackingData = [...trackingData].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );

      // ========================================================
      // CONVERT TRACKING DATA TO COORDINATES
      // ========================================================

      const coordinates = sortedTrackingData
        .map((entry) => {
          if (
            entry.tracking_coordinates?.latitude &&
            entry.tracking_coordinates?.longitude
          ) {
            return {
              lat: parseFloat(entry.tracking_coordinates.latitude),
              lng: parseFloat(entry.tracking_coordinates.longitude),

              locationName: entry.id_user?.name || "Unknown User",

              timestamp: moment(entry.createdAt).format("YYYY-MM-DD HH:mm:ss"),
            };
          }

          return null;
        })
        .filter(Boolean);

      console.log("Tracking points:", coordinates.length);

      setPathCoordinates(coordinates);

      // Clear old route
      setDirections([]);

      // Clear previous selected marker
      setSelectedMarker(null);
    } catch (error) {
      console.error("Error fetching user tracking data:", error);

      setPathCoordinates([]);
      setDirections([]);
      setSelectedMarker(null);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FETCH SELECTED DATE
  // ============================================================

  useEffect(() => {
    const selectedDate = location.state?.selectedDate;

    const dateToFetch = selectedDate || moment().format("YYYY-MM-DD");

    console.log("Selected date received by Map:", dateToFetch);

    fetchUserTrackingByDate(dateToFetch);
  }, [userId, location.state?.selectedDate]);

  // ============================================================
  // SPLIT COORDINATES INTO CHUNKS
  // ============================================================

  const chunkCoordinates = (coords, chunkSize = 25) => {
    const chunks = [];

    if (coords.length < 2) {
      return chunks;
    }

    /*
      Keep one common point between chunks.

      Example:

      Chunk 1:
      1 2 3 ... 25

      Chunk 2:
      25 26 27 ... 49

      This prevents gaps in the route.
    */

    for (let i = 0; i < coords.length - 1; i += chunkSize - 1) {
      const chunk = coords.slice(i, i + chunkSize);

      if (chunk.length >= 2) {
        chunks.push(chunk);
      }
    }

    return chunks;
  };

  // ============================================================
  // FETCH PROPER ROAD ROUTE
  // ============================================================

  const fetchDirections = async (coordinates) => {
    if (!googleReady || !window.google || coordinates.length < 2) {
      return;
    }

    try {
      const chunks = chunkCoordinates(coordinates, 25);

      console.log("Total route chunks:", chunks.length);

      const directionsService = new window.google.maps.DirectionsService();

      const results = [];

      // ========================================================
      // FETCH EACH ROUTE CHUNK
      // ========================================================

      for (const chunk of chunks) {
        const origin = chunk[0];

        const destination = chunk[chunk.length - 1];

        const waypoints = chunk.slice(1, -1).map((coord) => ({
          location: {
            lat: coord.lat,
            lng: coord.lng,
          },
          stopover: false,
        }));

        const result = await new Promise((resolve) => {
          directionsService.route(
            {
              origin: {
                lat: origin.lat,
                lng: origin.lng,
              },

              destination: {
                lat: destination.lat,
                lng: destination.lng,
              },

              waypoints,

              travelMode: window.google.maps.TravelMode.DRIVING,

              // VERY IMPORTANT
              // Keep the tracking point order.
              optimizeWaypoints: false,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                resolve(result);
              } else {
                console.error("Directions API error:", status);

                resolve(null);
              }
            },
          );
        });

        if (result) {
          results.push(result);
        }
      }

      console.log("Successfully generated routes:", results.length);

      setDirections(results);
    } catch (error) {
      console.error("Error fetching directions:", error);

      setDirections([]);
    }
  };

  // ============================================================
  // GENERATE ROUTE AFTER GOOGLE MAPS IS READY
  // ============================================================

  useEffect(() => {
    if (googleReady && pathCoordinates.length > 1) {
      fetchDirections(pathCoordinates);
    }
  }, [pathCoordinates, googleReady]);

  // ============================================================
  // FIT MAP TO COMPLETE ROUTE
  // ============================================================

  useEffect(() => {
    if (mapRef.current && pathCoordinates.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();

      pathCoordinates.forEach((coordinate) => {
        bounds.extend({
          lat: coordinate.lat,
          lng: coordinate.lng,
        });
      });

      mapRef.current.fitBounds(bounds);
    }
  }, [pathCoordinates]);

  // ============================================================
  // MARKER CLICK
  // ============================================================

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  // ============================================================
  // CLOSE INFO WINDOW
  // ============================================================

  const handleInfoWindowCloseClick = () => {
    setSelectedMarker(null);
  };

  // ============================================================
  // MAP
  // ============================================================

  return (
    <>
      <Layouts />

      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_BASE_API}
        onLoad={() => {
          console.log("Google Maps loaded");
          setGoogleReady(true);
        }}
      >
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={
            pathCoordinates[0] || {
              lat: 30.7067034,
              lng: 76.6877915,
            }
          }
          zoom={12}
          onLoad={(map) => {
            mapRef.current = map;
          }}
        >
          {loading ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "#ffffff",
                padding: "12px 20px",
                borderRadius: "8px",
                fontWeight: "600",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                zIndex: 1000,
              }}
            >
              Loading tracking...
            </div>
          ) : (
            <>
              {/* ==================================================
                  FLICKERING EFFECT AT LAST POINT
              =================================================== */}

              {pathCoordinates.length > 0 && (
                <OverlayView
                  position={pathCoordinates[pathCoordinates.length - 1]}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div style={rippleStyle}></div>
                </OverlayView>
              )}

              {/* ==================================================
                  TRACKING MARKERS
              =================================================== */}

              {pathCoordinates.map((coord, index) => (
                <Marker
                  key={index}
                  position={{
                    lat: coord.lat,
                    lng: coord.lng,
                  }}
                  label={(index + 1).toString()}
                  onClick={() => handleMarkerClick(coord)}
                />
              ))}

              {/* ==================================================
                  BLUE ROAD ROUTE
              =================================================== */}

              {Array.isArray(directions) &&
                directions.map((direction, index) => (
                  <DirectionsRenderer
                    key={index}
                    directions={direction}
                    options={{
                      // Hide Google's default A/B markers
                      suppressMarkers: true,

                      // Keep current map viewport
                      preserveViewport: true,

                      // Explicit BLUE route
                      polylineOptions: {
                        strokeColor: "#4285F4",
                        strokeOpacity: 0.9,
                        strokeWeight: 5,
                      },
                    }}
                  />
                ))}

              {/* ==================================================
                  INFO WINDOW
              =================================================== */}

              {selectedMarker && (
                <InfoWindow
                  position={{
                    lat: selectedMarker.lat,
                    lng: selectedMarker.lng,
                  }}
                  onCloseClick={handleInfoWindowCloseClick}
                >
                  <div
                    style={{
                      minWidth: "220px",
                      padding: "4px",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                      }}
                    >
                      {selectedMarker.locationName}
                    </h4>

                    <p
                      style={{
                        margin: "4px 0",
                      }}
                    >
                      <b>Coordinates:</b>
                      <br />
                      {selectedMarker.lat}, {selectedMarker.lng}
                    </p>

                    <p
                      style={{
                        margin: "4px 0",
                      }}
                    >
                      <b>Time:</b>
                      <br />
                      {selectedMarker.timestamp}
                    </p>
                  </div>
                </InfoWindow>
              )}
            </>
          )}
        </GoogleMap>
      </LoadScript>
    </>
  );
}
